// Admin > Surveys: surveys (create from a template or from scratch, edit, activate / pause / close,
// run, send, duplicate, delete, results), survey templates (add / edit / duplicate / delete), and
// the switch for survey messages (email / WhatsApp; while off they are recorded as held).
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem, Alert, Grid, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tabs, Tab, Tooltip, Switch, FormControlLabel, Checkbox,
  LinearProgress, Card, CardContent, CardActions, FormGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BarChartIcon from '@mui/icons-material/BarChart';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import {
  SURVEY_SETUP_QUERY, SURVEY_TEMPLATES_QUERY, SURVEYS_QUERY, SAVE_SURVEY_TEMPLATE_MUTATION, DELETE_SURVEY_TEMPLATE_MUTATION,
  SAVE_SURVEY_MUTATION, SET_SURVEY_STATUS_MUTATION, DUPLICATE_SURVEY_MUTATION, DELETE_SURVEY_MUTATION, RUN_SURVEY_NOW_MUTATION,
  SEND_SURVEY_NOW_MUTATION, SET_SURVEY_SENDING_MUTATION, SURVEY_PEOPLE_QUERY,
} from '../graphql/queries';
import PersonSurveys from '../components/PersonSurveys';
import SurveyQuestionBuilder from '../components/SurveyQuestionBuilder';
import SurveyForm from '../components/SurveyForm';
import SurveyResults from '../components/SurveyResults';

const STATUS_COLOR = { draft: 'default', active: 'success', paused: 'warning', closed: 'default' };
const CATEGORIES = [['wedding', 'Wedding'], ['vendor_service', 'Vendor service'], ['vendor_partner', 'Vendor partner'], ['onboarding', 'Onboarding'], ['general', 'General']];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const EMPTY_SURVEY = {
  title: '', description: '', introMessage: '', thankYouMessage: 'Thank you for your feedback!', questions: [], audienceRoles: ['bride_family', 'groom_family'],
  triggerType: 'after_event_date', triggerDelayDays: 2, scheduledAt: '', includePast: false, channels: ['email', 'whatsapp', 'in_app'],
  reminderDays: 3, maxReminders: 2, expiresAfterDays: 14, anonymous: false,
};
// What the trigger asks of the audience
const TRIGGER_HINT = {
  after_event_date: 'Families (and their matchmaker) are invited the set number of days after the wedding date in their Service Wizard.',
  after_booking_date: 'After each confirmed vendor booking’s date: families are asked about the vendor, vendors about the booking.',
  after_contract_accepted: 'The contract’s party is invited after accepting it.',
  after_signup: 'New accounts are invited the set number of days after signing up.',
  scheduled: 'Everyone in the audience is invited on the chosen date.',
  manual: 'Nothing is sent automatically: use “Send now” to invite the audience.',
};

function RoleChecks({ setup, value, onChange }) {
  return (
    <FormGroup row>
      {(setup?.roles || []).map(r => (
        <FormControlLabel key={r.key} label={r.label} control={
          <Checkbox checked={value.includes(r.key)} onChange={e => onChange(e.target.checked ? [...value, r.key] : value.filter(x => x !== r.key))} />
        } />
      ))}
    </FormGroup>
  );
}

// ── Survey editor ──────────────────────────────────────────────────────────────
function SurveyEditor({ initial, setup, onClose, onSaved }) {
  const [v, setV] = useState(() => ({ ...EMPTY_SURVEY, ...initial, scheduledAt: initial?.scheduledAt ? initial.scheduledAt.slice(0, 16) : '' }));
  const [tab, setTab] = useState(0);
  const [preview, setPreview] = useState({});
  const [save, { loading, error }] = useMutation(SAVE_SURVEY_MUTATION);
  const set = (k) => (e) => setV(x => ({ ...x, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const dated = !['manual', 'scheduled'].includes(v.triggerType);
  const go = async () => {
    const input = { ...v, scheduledAt: v.triggerType === 'scheduled' && v.scheduledAt ? new Date(v.scheduledAt).toISOString() : null };
    ['stats', 'templateName', 'triggerLabel', 'createdAt', 'updatedAt', 'lastRunAt', 'activatedAt', 'closedAt'].forEach(k => delete input[k]);
    const { data } = await save({ variables: { input } });
    onSaved(data.saveSurvey);
  };
  return (
    <Dialog open fullScreen onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>{v.id ? 'Edit survey' : 'New survey'}{v.templateName ? ` · from “${v.templateName}”` : ''}</Typography>
        {v.status === 'active' && <Chip size="small" color="warning" label="Active: changes apply to new answers" />}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, t) => setTab(t)} sx={{ mb: 2 }} variant="scrollable">
          <Tab label="1. Details" /><Tab label={`2. Questions (${v.questions.filter(q => q.type !== 'section').length})`} /><Tab label="3. Audience & timing" /><Tab label="4. Delivery" /><Tab label="5. Preview" />
        </Tabs>
        <Box sx={{ maxWidth: 980 }}>
          {tab === 0 && (
            <Stack spacing={2}>
              <TextField label="Title *" value={v.title} onChange={set('title')} fullWidth />
              <TextField label="Internal description" value={v.description || ''} onChange={set('description')} fullWidth multiline minRows={2} helperText="Only admins see this" />
              <TextField label="Message at the top of the survey" value={v.introMessage || ''} onChange={set('introMessage')} fullWidth multiline minRows={2} />
              <TextField label="Thank-you message" value={v.thankYouMessage || ''} onChange={set('thankYouMessage')} fullWidth />
              <FormControlLabel control={<Switch checked={Boolean(v.anonymous)} onChange={set('anonymous')} />} label="Anonymous: results do not show who answered" />
            </Stack>
          )}
          {tab === 1 && <SurveyQuestionBuilder questions={v.questions} onChange={questions => setV(x => ({ ...x, questions }))} types={setup?.questionTypes} operators={setup?.operators} />}
          {tab === 2 && (
            <Stack spacing={2.5}>
              <Box>
                <Typography fontWeight={700}>Who answers</Typography>
                <RoleChecks setup={setup} value={v.audienceRoles} onChange={audienceRoles => setV(x => ({ ...x, audienceRoles }))} />
                <Typography variant="caption" color="text.secondary">Deactivated accounts and hidden demo accounts are never surveyed.</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="When to send" value={v.triggerType} onChange={set('triggerType')} helperText={TRIGGER_HINT[v.triggerType]}>
                    {(setup?.triggers || []).map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                  </TextField>
                </Grid>
                {dated && (
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth type="number" label="Days after the date" value={v.triggerDelayDays} onChange={set('triggerDelayDays')} inputProps={{ min: 0, max: 365 }} helperText="0 = on the day" />
                  </Grid>
                )}
                {v.triggerType === 'scheduled' && (
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth type="datetime-local" label="Send on" value={v.scheduledAt} onChange={set('scheduledAt')} InputLabelProps={{ shrink: true }} />
                  </Grid>
                )}
              </Grid>
              {dated && (
                <FormControlLabel control={<Checkbox checked={Boolean(v.includePast)} onChange={set('includePast')} />}
                  label="Also include dates that passed before the survey was activated" />
              )}
              <Typography variant="caption" color="text.secondary">Each person is invited once per wedding, booking or contract. The schedule runs every hour; “Run now” runs it at once.</Typography>
            </Stack>
          )}
          {tab === 3 && (
            <Stack spacing={2.5}>
              <Box>
                <Typography fontWeight={700}>Send by</Typography>
                <FormGroup row>
                  {(setup?.channels || []).map(c => (
                    <FormControlLabel key={c.key} label={c.label} control={<Checkbox checked={v.channels.includes(c.key)} onChange={e => setV(x => ({ ...x, channels: e.target.checked ? [...x.channels, c.key] : x.channels.filter(y => y !== c.key) }))} />} />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="text.secondary">
                  Email and WhatsApp use the “Survey invitation” templates (Admin → Message Templates). WhatsApp needs a Meta-approved template; until then each one is a link to send by hand.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Remind after (days)" value={v.reminderDays} onChange={set('reminderDays')} inputProps={{ min: 0, max: 60 }} helperText="0 = no reminders" /></Grid>
                <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Reminders at most" value={v.maxReminders} onChange={set('maxReminders')} inputProps={{ min: 0, max: 5 }} /></Grid>
                <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Closes after (days)" value={v.expiresAfterDays} onChange={set('expiresAfterDays')} inputProps={{ min: 0, max: 365 }} helperText="0 = stays open" /></Grid>
              </Grid>
            </Stack>
          )}
          {tab === 4 && (
            <Paper variant="outlined" sx={{ p: 3, bgcolor: '#fdf6ff' }}>
              <Typography variant="h5" fontWeight={700}>{v.title || 'Untitled survey'}</Typography>
              {v.introMessage && <Typography sx={{ whiteSpace: 'pre-line' }} my={1}>{v.introMessage}</Typography>}
              <Box mt={2}><SurveyForm questions={v.questions} answers={preview} onChange={setPreview} /></Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={2}>Preview only: answers here are not saved. Conditions work as they will for respondents.</Typography>
            </Paper>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tab < 4 && <Button onClick={() => setTab(t => t + 1)}>Next</Button>}
        <Button variant="contained" disabled={loading} onClick={go}>{loading ? 'Saving…' : 'Save survey'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Template editor ────────────────────────────────────────────────────────────
function TemplateEditor({ initial, setup, onClose, onSaved }) {
  const [t, setT] = useState(() => ({ name: '', description: '', category: 'general', audienceRoles: [], questions: [], isActive: true, ...initial }));
  const [preview, setPreview] = useState(false);
  const [answers, setAnswers] = useState({});
  const [save, { loading, error }] = useMutation(SAVE_SURVEY_TEMPLATE_MUTATION);
  const go = async () => {
    const { usedBy, createdAt, updatedAt, isSystem, ...input } = t;
    const { data } = await save({ variables: { input } });
    onSaved(data.saveSurveyTemplate);
  };
  return (
    <Dialog open fullScreen onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>{t.id ? 'Edit template' : 'New template'}</Typography>
        <FormControlLabel control={<Switch checked={preview} onChange={e => setPreview(e.target.checked)} />} label="Preview" />
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxWidth: 980 }}>
          {!preview ? (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField fullWidth label="Template name *" value={t.name} onChange={e => setT(x => ({ ...x, name: e.target.value }))} /></Grid>
                <Grid item xs={6} md={3}>
                  <TextField select fullWidth label="Category" value={t.category} onChange={e => setT(x => ({ ...x, category: e.target.value }))}>
                    {CATEGORIES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} md={3}><FormControlLabel control={<Switch checked={t.isActive} onChange={e => setT(x => ({ ...x, isActive: e.target.checked }))} />} label="In use" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Description" value={t.description || ''} onChange={e => setT(x => ({ ...x, description: e.target.value }))} /></Grid>
              </Grid>
              <Box>
                <Typography variant="body2" fontWeight={700}>Suggested audience</Typography>
                <RoleChecks setup={setup} value={t.audienceRoles} onChange={audienceRoles => setT(x => ({ ...x, audienceRoles }))} />
              </Box>
              <SurveyQuestionBuilder questions={t.questions} onChange={questions => setT(x => ({ ...x, questions }))} types={setup?.questionTypes} operators={setup?.operators} />
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, bgcolor: '#fdf6ff' }}><SurveyForm questions={t.questions} answers={answers} onChange={setAnswers} /></Paper>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={go}>{loading ? 'Saving…' : 'Save template'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Everyone invited to a survey: open a person to see all their invitations and answers
function People() {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [person, setPerson] = useState(null);
  const { data, loading } = useQuery(SURVEY_PEOPLE_QUERY, { variables: { search: term }, fetchPolicy: 'cache-and-network' });
  const rows = data?.surveyPeople || [];
  return (
    <>
      <Stack direction="row" spacing={1} mb={1.5}>
        <TextField size="small" placeholder="Search by name or email" value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') setTerm(search); }} sx={{ minWidth: 320 }} />
        <Button onClick={() => setTerm(search)}>Search</Button>
      </Stack>
      <Paper variant="outlined">
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead><TableRow><TableCell>Person</TableCell><TableCell>Role</TableCell><TableCell align="right">Invited</TableCell><TableCell align="right">Answered</TableCell><TableCell>Last invited</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rows.map(p => (
              <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => setPerson(p.id)}>
                <TableCell><Typography variant="body2" fontWeight={600}>{p.name}</Typography><Typography variant="caption" color="text.secondary">{p.email}</Typography></TableCell>
                <TableCell>{p.roleLabel}</TableCell>
                <TableCell align="right">{p.invited}</TableCell>
                <TableCell align="right">{p.answered}</TableCell>
                <TableCell>{fmt(p.lastInvited)}</TableCell>
                <TableCell align="right"><Button size="small">View surveys</Button></TableCell>
              </TableRow>
            ))}
            {!loading && !rows.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>{term ? 'Nobody matches.' : 'Nobody has been invited to a survey yet.'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      {person && <PersonSurveys userId={person} onClose={() => setPerson(null)} />}
    </>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function AdminSurveysPage() {
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(null);       // survey
  const [editingTpl, setEditingTpl] = useState(null); // template
  const [picking, setPicking] = useState(false);
  const [results, setResults] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sendingConfirm, setSendingConfirm] = useState(false);
  const { data: setupData, refetch: refetchSetup } = useQuery(SURVEY_SETUP_QUERY);
  const { data, loading, refetch } = useQuery(SURVEYS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: tplData, refetch: refetchTpl } = useQuery(SURVEY_TEMPLATES_QUERY, { fetchPolicy: 'cache-and-network' });
  const [setStatus] = useMutation(SET_SURVEY_STATUS_MUTATION);
  const [duplicate] = useMutation(DUPLICATE_SURVEY_MUTATION);
  const [remove] = useMutation(DELETE_SURVEY_MUTATION);
  const [runNow] = useMutation(RUN_SURVEY_NOW_MUTATION);
  const [sendNow] = useMutation(SEND_SURVEY_NOW_MUTATION);
  const [removeTpl] = useMutation(DELETE_SURVEY_TEMPLATE_MUTATION);
  const [setSending, { loading: switching }] = useMutation(SET_SURVEY_SENDING_MUTATION);
  const setup = setupData?.surveySetup;
  const sending = setup?.settings?.sendingEnabled;
  const surveys = data?.surveys || [];
  const templates = tplData?.surveyTemplates || [];
  const roleLabel = Object.fromEntries((setup?.roles || []).map(r => [r.key, r.label]));

  const act = async (fn, ok) => {
    try { const r = await fn(); setNotice({ severity: 'success', text: typeof ok === 'function' ? ok(r) : ok }); refetch(); refetchTpl(); }
    catch (e) { setNotice({ severity: 'error', text: e.message }); }
  };
  const runSummary = (x) => `${x.created} new invitation(s) · ${x.sent} sent · ${x.held} held · ${x.failed} failed · ${x.reminded} reminder(s) · ${x.expired} closed`;
  const fromTemplate = (t) => { setPicking(false); setEditing({ ...EMPTY_SURVEY, title: t.name, templateId: t.id, templateName: t.name, questions: t.questions, audienceRoles: t.audienceRoles?.length ? t.audienceRoles : EMPTY_SURVEY.audienceRoles }); };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Surveys</Typography>
          <Typography variant="body2" color="text.secondary">Feedback from families, vendors and matchmakers, sent automatically when a date passes, or on demand.</Typography>
        </Box>
        <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={700}>Survey messages (email, WhatsApp)</Typography>
            <Typography variant="caption" color="text.secondary">{sending ? 'On: invitations are sent' : 'Off: recorded as held; in-app notices still show'}</Typography>
          </Box>
          <Switch checked={Boolean(sending)} disabled={switching}
            onChange={e => (e.target.checked ? setSendingConfirm(true) : setSending({ variables: { enabled: false } }).then(() => refetchSetup()))} />
        </Paper>
      </Stack>
      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice.text}</Alert>}
      <Tabs value={tab} onChange={(_, t) => setTab(t)} sx={{ mb: 2 }}>
        <Tab label={`Surveys (${surveys.length})`} />
        <Tab label={`Templates (${templates.length})`} />
        <Tab label="People" />
      </Tabs>
      {tab === 2 && <People />}

      {tab === 0 && (
        <>
          <Stack direction="row" justifyContent="flex-end" mb={1.5}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPicking(true)}>New survey</Button>
          </Stack>
          <Paper variant="outlined">
            {loading && <LinearProgress />}
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Survey</TableCell><TableCell>Audience</TableCell><TableCell>Status</TableCell><TableCell align="right">Invited</TableCell><TableCell align="right">Responses</TableCell><TableCell align="right">Avg rating</TableCell><TableCell align="right">Actions</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {surveys.map(sv => (
                  <TableRow key={sv.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{sv.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sv.triggerLabel}{!['manual', 'scheduled'].includes(sv.triggerType) ? `: ${sv.triggerDelayDays} day(s)` : sv.triggerType === 'scheduled' ? `: ${fmt(sv.scheduledAt)}` : ''}
                        {' · '}{sv.channels.map(c => c.replace('_', '-')).join(', ')}{sv.lastRunAt ? ` · last run ${fmt(sv.lastRunAt)}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell><Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>{sv.audienceRoles.map(r => <Chip key={r} size="small" variant="outlined" label={roleLabel[r] || r} />)}</Stack></TableCell>
                    <TableCell><Chip size="small" label={sv.status} color={STATUS_COLOR[sv.status]} />{sv.stats.held > 0 && <Typography variant="caption" color="text.secondary" display="block">{sv.stats.held} held</Typography>}</TableCell>
                    <TableCell align="right">{sv.stats.invited}</TableCell>
                    <TableCell align="right">{sv.stats.responses}<Typography variant="caption" color="text.secondary" display="block">{sv.stats.responseRate}%</Typography></TableCell>
                    <TableCell align="right">{sv.stats.ratingAvg ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Results"><IconButton size="small" onClick={() => setResults(sv.id)}><BarChartIcon fontSize="small" /></IconButton></Tooltip>
                      {sv.status !== 'closed' && <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(sv)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                      {['draft', 'paused'].includes(sv.status) && <Tooltip title={sv.status === 'draft' ? 'Activate' : 'Resume'}><IconButton size="small" color="success" onClick={() => act(() => setStatus({ variables: { id: sv.id, status: 'active' } }), `“${sv.title}” is active. The schedule checks it every hour.`)}><PlayArrowIcon fontSize="small" /></IconButton></Tooltip>}
                      {sv.status === 'active' && <Tooltip title="Pause"><IconButton size="small" onClick={() => act(() => setStatus({ variables: { id: sv.id, status: 'paused' } }), 'Survey paused.')}><PauseIcon fontSize="small" /></IconButton></Tooltip>}
                      {sv.status === 'active' && <Tooltip title="Run the schedule now"><IconButton size="small" onClick={() => act(() => runNow({ variables: { id: sv.id } }), r => runSummary(r.data.runSurveyNow))}><SyncIcon fontSize="small" /></IconButton></Tooltip>}
                      {sv.status === 'active' && <Tooltip title="Send now to the whole audience"><IconButton size="small" color="primary" onClick={() => setConfirm({ kind: 'send', sv })}><SendIcon fontSize="small" /></IconButton></Tooltip>}
                      <Tooltip title="Duplicate"><IconButton size="small" onClick={() => act(() => duplicate({ variables: { id: sv.id } }), 'Copied as a draft.')}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                      {sv.status !== 'closed' && sv.status !== 'draft' && <Tooltip title="Close"><IconButton size="small" onClick={() => setConfirm({ kind: 'close', sv })}><StopIcon fontSize="small" /></IconButton></Tooltip>}
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setConfirm({ kind: 'delete', sv })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && !surveys.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No surveys yet. Create one from a template.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {tab === 1 && (
        <>
          <Stack direction="row" justifyContent="flex-end" mb={1.5}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditingTpl({})}>New template</Button>
          </Stack>
          <Grid container spacing={2}>
            {templates.map(t => (
              <Grid item xs={12} md={6} lg={4} key={t.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: t.isActive ? 1 : 0.6 }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <FactCheckIcon color="primary" fontSize="small" />
                      <Typography fontWeight={700} sx={{ flex: 1 }}>{t.name}</Typography>
                      {t.isSystem && <Chip size="small" variant="outlined" label="Ready-made" />}
                      {!t.isActive && <Chip size="small" label="Not in use" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={1}>{t.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.questions.filter(q => q.type !== 'section').length} questions · {(CATEGORIES.find(c => c[0] === t.category) || [null, t.category])[1]}
                      {t.audienceRoles.length ? ` · ${t.audienceRoles.map(r => roleLabel[r] || r).join(', ')}` : ''} · used by {t.usedBy} survey(s)
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" variant="contained" disabled={!t.isActive} onClick={() => fromTemplate(t)}>Use</Button>
                    <Button size="small" onClick={() => setEditingTpl(t)}>Edit</Button>
                    <Button size="small" onClick={() => setEditingTpl({ ...t, id: undefined, name: `${t.name} (copy)`, isSystem: false })}>Duplicate</Button>
                    <Button size="small" color="error" onClick={() => setConfirm({ kind: 'deleteTpl', t })}>Delete</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Choose how to start a survey */}
      <Dialog open={picking} onClose={() => setPicking(false)} maxWidth="md" fullWidth>
        <DialogTitle>Start a survey</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', height: '100%', '&:hover': { borderColor: 'primary.main' } }} onClick={() => { setPicking(false); setEditing({ ...EMPTY_SURVEY }); }}>
                <Typography fontWeight={700}>Blank survey</Typography>
                <Typography variant="body2" color="text.secondary">Build the questions yourself</Typography>
              </Paper>
            </Grid>
            {templates.filter(t => t.isActive).map(t => (
              <Grid item xs={12} sm={6} key={t.id}>
                <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', height: '100%', '&:hover': { borderColor: 'primary.main' } }} onClick={() => fromTemplate(t)}>
                  <Typography fontWeight={700}>{t.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.questions.filter(q => q.type !== 'section').length} questions</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Confirmations */}
      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirm?.kind === 'send' ? 'Send now?' : confirm?.kind === 'close' ? 'Close survey?' : confirm?.kind === 'deleteTpl' ? 'Delete template?' : 'Delete survey?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.kind === 'send' && <>Invites everyone in the audience of “{confirm.sv.title}” who has not been invited yet{sending ? '.' : '. Survey messages are off, so email and WhatsApp are held; in-app notices show.'}</>}
            {confirm?.kind === 'close' && <>“{confirm.sv.title}” stops collecting answers and open invitations close. Results stay. A closed survey cannot be reopened.</>}
            {confirm?.kind === 'delete' && <>“{confirm.sv.title}” and its {confirm.sv.stats.responses} response(s) are deleted permanently.</>}
            {confirm?.kind === 'deleteTpl' && <>“{confirm.t.name}” is deleted. Surveys made from it keep their own questions.</>}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={['delete', 'deleteTpl'].includes(confirm?.kind) ? 'error' : 'primary'} onClick={() => {
            const c = confirm; setConfirm(null);
            if (c.kind === 'send') act(() => sendNow({ variables: { id: c.sv.id } }), r => `${r.data.sendSurveyNow.audience} in the audience · ${runSummary(r.data.sendSurveyNow)}`);
            if (c.kind === 'close') act(() => setStatus({ variables: { id: c.sv.id, status: 'closed' } }), 'Survey closed.');
            if (c.kind === 'delete') act(() => remove({ variables: { id: c.sv.id } }), 'Survey deleted.');
            if (c.kind === 'deleteTpl') act(() => removeTpl({ variables: { id: c.t.id } }), 'Template deleted.');
          }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendingConfirm} onClose={() => setSendingConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Switch survey messages on?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>Active surveys then email and WhatsApp their invitations and reminders, including any held so far.</Typography>
          <Typography variant="body2" color="text.secondary">Email needs Admin → Email Settings. WhatsApp sends automatically only with a Meta-approved “survey_invitation” template; otherwise each is a link to send by hand.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendingConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => { setSendingConfirm(false); await setSending({ variables: { enabled: true } }); refetchSetup(); setNotice({ severity: 'success', text: 'Survey messages are on.' }); }}>Switch on</Button>
        </DialogActions>
      </Dialog>

      {editing && <SurveyEditor initial={editing} setup={setup} onClose={() => setEditing(null)} onSaved={(sv) => { setEditing(null); refetch(); setNotice({ severity: 'success', text: `Saved “${sv.title}”.${sv.status === 'draft' ? ' Activate it to start sending.' : ''}` }); }} />}
      {editingTpl && <TemplateEditor initial={editingTpl} setup={setup} onClose={() => setEditingTpl(null)} onSaved={(t) => { setEditingTpl(null); refetchTpl(); setNotice({ severity: 'success', text: `Template “${t.name}” saved.` }); }} />}
      {results && <SurveyResults surveyId={results} onClose={() => setResults(null)} />}
    </Box>
  );
}
